import {
  isExplicitPort,
  isPrivateIpPort,
  NetworkAttachments,
} from '../CreateVirtualMachineModal.helpers'
import { PortsMap } from '../hooks/useNetworkPorts'

export interface VMCreationParams {
  name: string
  hostname: string
  description: string
  sourceType: string
  image: any[]
  installerIso: any[]
  driverIso: any[]
  volume: any[]
  volumeSize: string
  volumeType: string
  volumeSnapshot: any[]
  deleteOnTermination: boolean
  deleteOnTerminationAdditionalVolumes: boolean
  flavor: any[]
  networks: any[]
  // FR-016: the attachment rows for each managed network — each row becomes one network entry sent to Nova
  networkAttachments?: NetworkAttachments
  // Older one-port-per-network maps still used by the Clone VM modal
  selectedPorts: { [networkId: string]: string }
  fixedIPs: { [networkId: string]: string }
  attachVolumes: any[]
  sshKey: string | null
  serverGroup: { id: string } | null
  useCloudInit: boolean
  cloudInitYaml: string
  useSecurityGroups: boolean
  securityGroups: { id: string }[]
  metadata: { key: string; value: string }[]
  enableHotplugging: boolean
  hotplugMemory: number | null
  hotplugMemoryMax: number
  hotplugVcpu: number | null
  hotplugVcpuMax: number
  availabilityZone: string | null
  priority: string
  stretchedCluster: string
  count: number
  // PCD-8236 CVM-FR-026: 'create' means the flavor came from the custom-flavor form, which has
  // no hotplug controls — guards against stale HOTPLUG_* metadata from a prior 'existing' pick.
  flavorMode?: 'existing' | 'create'
}

export interface BuildVMBodyOptions {
  managedNetworkPorts?: PortsMap
  // PCD-8237 (IBV-FR-002): id of the boot volume pre-created for Install-from-ISO, already
  // stamped with the installer ISO's UEFI/Secure-Boot/TPM image metadata. When present, the
  // OS boot disk references this volume instead of being a blank device.
  isoBootVolumeId?: string
}

/** VM metadata key where we stash the ids of ports we made for simple (L2-only) networks, so we can delete those ports when the VM is deleted. */
export const LAYER_2_PORT_METADATA_KEY = 'layer-2-network-ports'

/** VM metadata key where we stash the ids of ports we made for managed networks (subnet-selected), so we can delete those ports when the VM is deleted. */
export const MANAGED_NETWORK_PORT_METADATA_KEY = 'managed-network-ports'

/**
 * PCD-5420: build the `server.networks` list Nova expects — one entry per network interface (NIC).
 * Both the Create and Clone flows call this so they produce the same correct output for VMs with
 * multiple subnets or ports.
 *
 * For each network it decides the entry shape:
 * - Simple (L2) network → use the port we created (`{ port }`), or just the network (`{ uuid }`).
 * - Managed network that has attachment rows → one entry per row, choosing per row:
 *   a port we created (`{ port }`), a port the user picked (`{ port }`),
 *   a manual fixed IP (`{ uuid, fixed_ip }`), or fully automatic (`{ uuid }`).
 * - Managed network with no attachment rows → fall back to the older single selectedPorts/fixedIPs maps.
 */
export const buildNetworkEntries = (
  params: Pick<VMCreationParams, 'networks' | 'networkAttachments' | 'selectedPorts' | 'fixedIPs'>,
  options: BuildVMBodyOptions = {},
): any[] => {
  const { managedNetworkPorts = {} } = options
  const entries: any[] = []

  for (const network of params.networks || []) {
    // Both managed and simple (L2) networks store their NIC(s) as attachment rows and flow through
    // the same logic below. A simple network is just the single-row, no-subnet case: its row is
    // either Automatic (→ { uuid }, Nova creates and owns the port) or a user-picked existing port
    // (→ { port }). We never pre-create L2 ports. [FR-024]
    // FR-016: one Nova entry per attachment row (each row is one NIC).
    const attachmentRows = params.networkAttachments?.[network.id]
    if (attachmentRows?.length) {
      for (const row of attachmentRows) {
        if (managedNetworkPorts[row.rowId]) {
          // We already created a port for this row (it had a subnet chosen + "Automatic port").
          entries.push({ port: managedNetworkPorts[row.rowId] })
        } else if (isExplicitPort(row.port)) {
          // The user picked a specific existing port for this row.
          entries.push({ port: row.port })
        } else if (isPrivateIpPort(row.port)) {
          // The user typed a specific IP address for this NIC.
          entries.push({ uuid: network.id, fixed_ip: row.fixedIP })
        } else {
          // Let Nova decide everything (no subnet chosen, or no port was pre-created).
          entries.push({ uuid: network.id })
        }
      }
      continue
    }

    // Fallback for callers (e.g. Clone VM) that don't pass networkAttachments.
    entries.push({
      uuid: network.id,
      ...(params.selectedPorts[network.id]
        ? isPrivateIpPort(params.selectedPorts[network.id])
          ? { fixed_ip: params.fixedIPs[network.id] }
          : { port: params.selectedPorts[network.id] }
        : {}),
    })
  }

  return entries
}

/**
 * Build the full "create server" request body that we send to the OpenStack Nova API.
 *
 * It assembles all the pieces of a new VM:
 * - Boot source (image, volume, or snapshot)
 * - Flavor (the CPU/RAM/disk size)
 * - Networking (including ports we pre-created for simple networks)
 * - Cloud-init user data (a startup script)
 * - Security groups (firewall rules)
 * - Metadata (extra key/value tags, including hotplug settings)
 * - Scheduler hints (e.g. which server group to place the VM in)
 */
export const buildVMRequestBody = (
  params: VMCreationParams,
  options: BuildVMBodyOptions = {},
): any => {
  const { managedNetworkPorts = {}, isoBootVolumeId } = options

  // Cloud-init expects the script base64-encoded; only include it when cloud-init is on.
  const encodedScript = params.useCloudInit ? btoa(params.cloudInitYaml) : undefined

  // Build the security groups (firewall rule sets) to attach.
  // FR-014: when the toggle is off, send an empty array so Nova doesn't auto-assign the default group.
  // CVM-FR-027: send the group's id in Nova's `name` field. Nova only finds a group by name inside
  // the VM's own project, so a group shared from another project works only by id (PCD-5869).
  const securityGroups = params.useSecurityGroups
    ? params.securityGroups.map((group) => ({ name: group.id }))
    : []

  // Turn the list of key/value pairs into a single metadata object.
  const metadata = (params.metadata || []).reduce(
    (accum, pair) => ({
      ...accum,
      [pair.key]: pair.value,
    }),
    {},
  )

  // If hotplugging (changing CPU/memory on a running VM) is on, record those values as metadata.
  // The *_MAX keys cap how far CPU/memory can be hotplugged — without them the
  // hypervisor defaults to the host maximums, which leaks memory on Windows guests.
  // CVM-FR-026: a custom flavor never exposes hotplug controls, so ignore any stale
  // enableHotplugging left over from a prior 'existing' selection.
  const hotplugMetadata =
    params.enableHotplugging && params.flavorMode !== 'create'
      ? {
          HOTPLUG_MEMORY: String(params.hotplugMemory),
          HOTPLUG_MEMORY_MAX: String(params.hotplugMemoryMax),
          HOTPLUG_CPU: String(params.hotplugVcpu),
          HOTPLUG_CPU_MAX: String(params.hotplugVcpuMax),
        }
      : {}

  // Save the ids of the managed ports we pre-created (comma-separated) so we can find and delete
  // them when the VM is deleted. (Simple/L2 ports are created and owned by Nova, so nothing to
  // track — Nova auto-deletes them.)
  const managedPortIds = Object.values(managedNetworkPorts).join(',') || undefined

  // Merge all the metadata pieces into one object.
  const combinedMetadata = {
    ...metadata,
    ...hotplugMetadata,
    ...(params.priority !== '' && { 'migration-priority': params.priority }),
    ...(params.stretchedCluster && { pf9_failure_domain: params.stretchedCluster }),
    ...(managedPortIds && { [MANAGED_NETWORK_PORT_METADATA_KEY]: managedPortIds }),
  }

  // Build the list of extra (non-boot) volumes to attach to the VM.
  const additionalVolumes = (params.attachVolumes || []).map((volume) => ({
    uuid: volume?.id,
    source_type: 'volume',
    destination_type: 'volume',
    boot_index: -1,
    delete_on_termination: params.deleteOnTerminationAdditionalVolumes,
  }))

  // Describe the VM's disks ("block device mapping") based on where it boots from.
  const buildBlockDeviceMapping = () => {
    // Booting straight from an image or snapshot needs no explicit disk mapping.
    if (['image', 'snapshot'].includes(params.sourceType)) {
      return undefined
    }

    if (params.sourceType === 'installFromIso') {
      // "Install from ISO" is really a 2-disk setup:
      // 1) Nova creates a small ISO-backed volume that behaves like a virtual CD/DVD
      // 2) Nova also creates a blank target volume where the OS will be installed
      const installerIsoSizeBytes = params.installerIso?.[0]?.virtual_size || 0
      const installerIsoVolumeSizeGiB = installerIsoSizeBytes
        ? Math.ceil(installerIsoSizeBytes / (1024 * 1024 * 1024))
        : 1
      // IBV-FR-002: when a boot volume was pre-created (with the ISO's UEFI/Secure-Boot/TPM
      // metadata), boot the OS target disk from that volume so Nova applies the firmware. With
      // no pre-created volume we fall back to the original blank device (unchanged behavior).
      const osTargetDisk = isoBootVolumeId
        ? {
            // OS target disk — boot from the pre-seeded volume (boot_index=0)
            uuid: isoBootVolumeId,
            source_type: 'volume',
            destination_type: 'volume',
            boot_index: 0,
            delete_on_termination: params.deleteOnTermination,
          }
        : {
            // OS target disk — blank device (boot_index=0)
            source_type: 'blank',
            destination_type: 'volume',
            volume_size: params.volumeSize,
            boot_index: 0,
            delete_on_termination: params.deleteOnTermination,
            volume_type: params.volumeType || undefined,
          }
      const isoVolumes = [
        osTargetDisk,
        {
          // Installer ISO — boot_index=1
          uuid: params.installerIso?.[0]?.id,
          source_type: 'image',
          destination_type: 'volume',
          volume_size: installerIsoVolumeSizeGiB,
          boot_index: 1,
          device_type: 'cdrom',
          delete_on_termination: true,
        },
      ]

      if (params.driverIso?.[0]?.id) {
        const driverIsoSizeBytes = params.driverIso[0]?.virtual_size || 0
        const driverIsoVolumeSizeGiB = driverIsoSizeBytes
          ? Math.ceil(driverIsoSizeBytes / (1024 * 1024 * 1024))
          : 1
        isoVolumes.push({
          uuid: params.driverIso[0].id,
          source_type: 'image',
          destination_type: 'volume',
          volume_size: driverIsoVolumeSizeGiB,
          boot_index: -1,
          device_type: 'cdrom',
          delete_on_termination: true,
        })
      }

      return [...isoVolumes, ...additionalVolumes]
    }

    const bootDevice =
      params.sourceType === 'existingVolume'
        ? [
            {
              source_type: 'volume',
              destination_type: 'volume',
              boot_index: 0,
              delete_on_termination: false,
              uuid: params.volume[0]?.id,
            },
          ]
        : params.sourceType === 'newVolume'
          ? [
              {
                source_type: 'image',
                destination_type: 'volume',
                boot_index: 0,
                delete_on_termination: params.deleteOnTermination,
                uuid: params.image?.[0]?.id,
                volume_size: params.volumeSize,
                volume_type: params.volumeType ? params.volumeType : undefined,
              },
            ]
          : params.sourceType === 'volumeSnapshot'
            ? [
                {
                  source_type: 'snapshot',
                  destination_type: 'volume',
                  boot_index: 0,
                  delete_on_termination: params.deleteOnTermination,
                  uuid: params.volumeSnapshot?.[0]?.id,
                },
              ]
            : []

    return [...bootDevice, ...additionalVolumes]
  }

  // Build the network entries (one per NIC). Shared with the Clone flow — see buildNetworkEntries.
  const buildNetworks = () => buildNetworkEntries(params, { managedNetworkPorts })

  // Decide whether to turn on config drive (a tiny disk Nova attaches to pass config/cloud-init
  // data to the VM when the network can't). We only need it when cloud-init is on AND either a
  // simple network is used OR some subnet has DHCP turned off (so the VM can't auto-get its config).
  const shouldEnableConfigDrive =
    params.useCloudInit &&
    params.networks.some(
      (network) =>
        network.isSimpleNetwork ||
        network.subnetDetails?.some((subnet: any) => subnet.enable_dhcp === false),
    )

  // Assemble and return the final request body sent to Nova.
  return {
    server: {
      name: params.name,
      description: params.description,
      imageRef: ['image', 'snapshot'].includes(params.sourceType)
        ? params.image?.[0]?.id
        : undefined,
      block_device_mapping_v2: buildBlockDeviceMapping(),
      flavorRef: params.flavor?.[0]?.id,
      networks: buildNetworks(),
      key_name: params.sshKey ? (params.sshKey === 'None' ? undefined : params.sshKey) : undefined,
      config_drive: shouldEnableConfigDrive,
      user_data: params.useCloudInit ? encodedScript : undefined,
      security_groups: securityGroups,
      metadata: combinedMetadata,
      ...(params.availabilityZone && { availability_zone: params.availabilityZone }),
      ...(params.hostname && { hostname: params.hostname }),
      min_count: params.count === 1 ? undefined : params.count,
      return_reservation_id: params.count === 1 ? undefined : true,
    },
    'OS-SCH-HNT:scheduler_hints': params.serverGroup
      ? { group: params.serverGroup?.id }
      : undefined,
  }
}

export default buildVMRequestBody
